/**
 * Steps v2 client selectors. `board.steps[]` + `board.currentStepId` are the
 * source of truth; everything the Do-next UI needs is derived from them here.
 * Mirrors the small read-only subset of server/routes/utils/steps/stepsModel.js.
 */

export function normalizeStepTitle(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').slice(0, 200);
}

export function stepTitleKey(value) {
  return normalizeStepTitle(
    value && typeof value === 'object' ? value.title || value.goal : value
  ).toLowerCase();
}

export function boardSteps(board) {
  return Array.isArray(board?.steps) ? board.steps.filter(Boolean) : [];
}

const STATUS_ORDER = { open: 0, finished: 1, not_interested: 2 };

export function sortSteps(steps) {
  return [...(steps || [])].sort((a, b) => {
    const s = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    if (s) return s;
    if (a.status === 'open' && Number(a.priority) !== Number(b.priority)) {
      return (Number(a.priority) || 99) - (Number(b.priority) || 99);
    }
    return new Date(b.at || 0) - new Date(a.at || 0);
  });
}

export function openSteps(board) {
  return sortSteps(boardSteps(board).filter(s => s.status === 'open'));
}

export function finishedSteps(board) {
  return sortSteps(boardSteps(board).filter(s => s.status === 'finished'));
}

export function hiddenSteps(board) {
  return sortSteps(boardSteps(board).filter(s => s.status === 'not_interested'));
}

export function findStep(board, idOrTitle) {
  const steps = boardSteps(board);
  const id = String(idOrTitle || '').trim();
  if (!id) return null;
  const byId = steps.find(s => s.id === id);
  if (byId) return byId;
  const key = stepTitleKey(id);
  return steps.find(s => stepTitleKey(s.title) === key) || null;
}

export function currentStep(board) {
  return findStep(board, board?.currentStepId) || null;
}

export function currentStepTitle(board) {
  return currentStep(board)?.title || String(board?.currentStep || '');
}

export function stepHasPack(step) {
  const pack = step?.pack;
  return Boolean(
    pack && ((Array.isArray(pack.blockers) && pack.blockers.length)
      || (Array.isArray(pack.nextActions) && pack.nextActions.length))
  );
}

/**
 * Legacy stepCandidate shape (title + child pack) for components that still
 * render candidates (plan modal, pin-case liveCoach payload).
 */
export function candidateFromStep(step) {
  if (!step) return null;
  const pack = step.pack || {};
  return {
    id: step.id,
    goal: step.title,
    reason: step.reason || '',
    priority: step.priority,
    progressSummary: step.progressSummary || '',
    achievementRate: step.achievementRate ?? null,
    status: step.status === 'finished' ? 'finished' : 'in_progress',
    packSource: step.packSource || null,
    blockers: Array.isArray(pack.blockers) ? pack.blockers : [],
    nextActions: Array.isArray(pack.nextActions) ? pack.nextActions : [],
    searchQueryCandidates: Array.isArray(pack.searchQueryCandidates) ? pack.searchQueryCandidates : [],
    geminiQuestionCandidates: Array.isArray(pack.geminiQuestionCandidates)
      ? pack.geminiQuestionCandidates
      : [],
    gaps: Array.isArray(pack.gaps) ? pack.gaps : [],
    createdByUsername: step.createdByUsername || '',
  };
}

/** Open steps as dropdown candidates, in priority order. */
export function stepChoicesFromBoard(board) {
  return openSteps(board).map(candidateFromStep).filter(Boolean);
}

// ---------------------------------------------------------------------------
// steps[] <-> contextRecommendations (client mirror of the server projection)
//
// `board.steps[]` + `board.currentStepId` are the truth. `contextRecommendations`
// is a derived view of the current step plus the step-independent ledger
// (constraints, pendingDigest, fingerprint, facets). Every local write goes
// through `applyRecsToBoard` so the dropdown and the blocker section always
// read the same step record.
// ---------------------------------------------------------------------------

const PACK_KEYS = [
  'blockers',
  'nextActions',
  'searchQueryCandidates',
  'geminiQuestionCandidates',
  'gaps',
  'workingConclusion',
  'validationQueue',
];

const PROGRESS_KEYS = [
  'aiAchievementRate',
  'doneBlockerKeys',
  'doneValidationKeys',
  'notInterestedKeys',
  'triedActionKeys',
  'doneFills',
  'partialBlockerKeys',
  'partialFills',
];

/** Fields of contextRecommendations that are not owned by a step. */
const LEDGER_KEYS = [
  'constraints',
  'pendingDigest',
  'sourceFingerprint',
  'at',
  'goalFacets',
  'coverage',
  'coachEmbeddings',
  'suggestionBatchCount',
];

function pickKeys(source, keys) {
  const out = {};
  if (!source || typeof source !== 'object') return out;
  for (const key of keys) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out;
}

/** Step progress record from `contextRecommendations.userProgress` (drops board-level keys). */
export function progressFromUserProgress(userProgress) {
  return pickKeys(userProgress, PROGRESS_KEYS);
}

function signatureKeyList(list) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(list) ? list : []) {
    const value = String(raw || '').trim().toLowerCase();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  out.sort();
  return out;
}

/**
 * Mirror of server `userProgressSignature`. Used to flip `userProgressDirty`
 * optimistically when Solved / Not interested is toggled.
 */
export function userProgressSignature(steps = []) {
  const lines = (Array.isArray(steps) ? steps : [])
    .filter(s => s && s.id)
    .map(s => {
      const p = s.progress && typeof s.progress === 'object' ? s.progress : {};
      const rate = Number.isFinite(Number(s.achievementRate))
        ? Math.max(0, Math.min(100, Math.round(Number(s.achievementRate))))
        : 0;
      return [
        String(s.id),
        String(s.status || 'open'),
        String(rate),
        signatureKeyList(p.doneBlockerKeys).join(','),
        signatureKeyList(p.notInterestedKeys).join(','),
        signatureKeyList(p.triedActionKeys).join(','),
        signatureKeyList(p.doneValidationKeys).join(','),
      ].join('|');
    })
    .sort();
  return lines.join('\n').slice(0, 4000);
}

/** Mirror of server `digestAfterUserProgressWrite`. */
export function digestAfterUserProgressWrite(pendingDigest, { priorSteps, nextSteps } = {}) {
  const prior = pendingDigest && typeof pendingDigest === 'object' ? pendingDigest : {};
  const baseline = String(prior.progressSig || '') || userProgressSignature(priorSteps);
  const userProgressDirty = userProgressSignature(nextSteps) !== baseline;
  const hasDigestWork = (Array.isArray(prior.events) && prior.events.length > 0)
    || Boolean(prior.deletedSinceStepRefresh);
  return {
    ...prior,
    progressSig: baseline,
    userProgressDirty,
    needsStepRefresh: hasDigestWork
      ? true
      : (userProgressDirty ? Boolean(prior.needsStepRefresh) : false),
  };
}

/**
 * Derive `contextRecommendations` from the current step + the prior ledger.
 * Mirrors server `projectLegacyFields`. Without a current step the prior
 * step-scoped fields are left as they were (nothing to project from).
 */
export function projectStepsToRecs(board) {
  const prev = board?.contextRecommendations && typeof board.contextRecommendations === 'object'
    ? board.contextRecommendations
    : {};
  const step = currentStep(board);
  const open = openSteps(board);
  const notInterestedGoalKeys = hiddenSteps(board).map(s => s.title);
  const shared = {
    ...pickKeys(prev, LEDGER_KEYS),
    stepCandidates: open.map(candidateFromStep),
    stepPriorities: open.map(s => ({
      goal: s.title,
      priority: s.priority,
      progressSummary: s.progressSummary || '',
    })),
  };
  if (!step) {
    return {
      ...prev,
      ...shared,
      userProgress: { ...(prev.userProgress || {}), notInterestedGoalKeys },
    };
  }
  const pack = step.pack || {};
  return {
    ...shared,
    blockers: Array.isArray(pack.blockers) ? pack.blockers : [],
    nextActions: Array.isArray(pack.nextActions) ? pack.nextActions : [],
    searchQueryCandidates: Array.isArray(pack.searchQueryCandidates) ? pack.searchQueryCandidates : [],
    geminiQuestionCandidates: Array.isArray(pack.geminiQuestionCandidates)
      ? pack.geminiQuestionCandidates
      : [],
    searchQueries: [],
    gaps: Array.isArray(pack.gaps) ? pack.gaps : [],
    workingConclusion: pack.workingConclusion || '',
    validationQueue: Array.isArray(pack.validationQueue) ? pack.validationQueue : [],
    progressSummary: step.progressSummary || '',
    achievementRate: step.achievementRate ?? null,
    userProgress: { ...progressFromUserProgress(step.progress), notInterestedGoalKeys },
    stepId: step.id,
    stepStatus: step.status,
  };
}

/**
 * Write the step-scoped parts of a recs-shaped object onto the current step.
 * Chips replace the pack only when the recs carry chips; progress is replaced
 * (local writers already compute the full next record, including removals).
 * Returns the same array when there is no current step.
 */
export function mirrorRecsIntoSteps(board, recs) {
  const steps = boardSteps(board);
  const step = currentStep(board);
  if (!step || !recs || typeof recs !== 'object') {
    return Array.isArray(board?.steps) ? board.steps : steps;
  }
  const packFields = pickKeys(recs, PACK_KEYS);
  const rate = Number(recs.achievementRate);
  return steps.map(s => {
    if (s.id !== step.id) return s;
    const next = { ...s };
    // A step that already has chips takes every pack field the recs carry
    // (so a local dismissal can empty a list); a pack-less step only gains a
    // pack when the recs actually contain chips.
    if (s.pack ? Object.keys(packFields).length : stepHasPack({ pack: packFields })) {
      next.pack = { ...(s.pack || {}), ...packFields };
    }
    if (recs.userProgress && typeof recs.userProgress === 'object') {
      next.progress = { ...(s.progress || {}), ...progressFromUserProgress(recs.userProgress) };
    }
    if (Number.isFinite(rate)) next.achievementRate = Math.max(0, Math.min(100, Math.round(rate)));
    if (recs.progressSummary) next.progressSummary = String(recs.progressSummary);
    return next;
  });
}

/**
 * Apply a local recs update to the board: mirror into the current step, then
 * re-project `contextRecommendations` from the steps so both views agree.
 */
export function applyRecsToBoard(board, recs) {
  const base = board && typeof board === 'object' ? board : {};
  const steps = mirrorRecsIntoSteps(base, recs);
  const withRecs = { ...base, steps, contextRecommendations: recs };
  return { ...withRecs, contextRecommendations: projectStepsToRecs(withRecs) };
}

/** Step payload fields the server returns after every steps write. */
export function pickStepsPayload(data) {
  if (!data || typeof data !== 'object') return null;
  if (!Array.isArray(data.steps)) return null;
  return {
    steps: data.steps,
    currentStepId: String(data.currentStepId || ''),
    currentStep: String(data.currentStep || ''),
    ...(data.contextRecommendations !== undefined
      ? { contextRecommendations: data.contextRecommendations }
      : {}),
    ...(Array.isArray(data.goalHistory) ? { goalHistory: data.goalHistory } : {}),
    ...(data.stepsAt !== undefined ? { stepsAt: data.stepsAt } : {}),
  };
}
